import { moduleLabel as vocabModuleLabel } from '../lib/riskVocabulary';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type RowSelectionState,
  type PaginationState,
} from '@tanstack/react-table';
import { Pagination } from './pagination';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getWithMeta, api } from '../api/client';
import type {
  ProjectAssetDetail,
  PaginatedMeta,
  ModuleCode,
  RiskLevel,
  LikertLevel,
  BulkUpdateResult,
} from '../api/types';
import { RiskBadge } from './risk-badge';
import { ThreatSelectionPanel } from './threat-selection-panel';
import { apiUrl } from '../api/client';
import {
  MODULE_INPUTS,
  parameterLabel,
} from '../lib/module-inputs';
import {
  COLUMN_GROUPS,
  useGridViewState,
  isColumnHidden,
  type ViewPreset,
} from './asset-grid-view-state';

/** Display label per module column group (from COLUMN_GROUPS; ids are identifiers). */
const GROUP_LABEL: Record<string, string> = Object.fromEntries(
  COLUMN_GROUPS.filter((g) => g.id !== 'group_properties').map((g) => [g.id, g.label]),
);

// =============================================================================
// Slice 6b: AssetGrid
//
// The portfolio surface. Replaces the per-card list with a virtualized
// table that handles thousands of rows: type-to-filter, sort by any
// column, multi-select for bulk operations, inline edit on common
// columns, click to drill into the asset.
//
// Data source: GET /projects/:id/assets?include=details (paginated by
// cursor). For Slice 6b we fetch the whole project (or up to 500 rows
// per page across multiple fetches) and filter client-side. Server-side
// filter pushdown is Slice 7+ when we have projects with > a few
// thousand assets.
// =============================================================================

const LIKERT_LEVELS: LikertLevel[] = ['Very_low', 'Low', 'Moderate', 'High', 'Very_high'];

// Column meta — TanStack supports arbitrary `meta` on column defs, but the
// type defaults to {}. This module augmentation tells TS that two specific
// fields are valid on AssetGrid columns. Hover-to-illuminate reads these.
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- type-system contract
  interface ColumnMeta<TData, TValue> {
    /** When the header for THIS module is hovered, a popover shows hidden inputs. */
    moduleCode?: ModuleCode;
    /** Modules whose hover-illuminate should highlight this column. */
    feedsModules?: ModuleCode[];
  }
}

interface Props {
  projectId: string;
  /** Module codes the project has in scope — drives which risk columns appear. */
  moduleScope: ModuleCode[];
}

const RISK_RANK: Record<RiskLevel, number> = {
  Critical: 7, Very_high: 6, High: 5, Moderate: 4, Low: 3, Very_low: 2, No_risk: 1, NA: 0,
};

// Module names come from the shared vocabulary (Sprint 1, D4).
function moduleLabel(code: ModuleCode): string {
  return vocabModuleLabel(code);
}

export function AssetGrid({ projectId, moduleScope }: Props) {
  const queryClient = useQueryClient();

  // ---------- table state ----------
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  // Server-side pagination: this drives the limit/offset sent to the API, and
  // the row count comes back with the page. Sorting, filtering and search are
  // server-side too (see serverParams below).
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 100 });

  // Search hits the server, so debounce it: one request when typing settles,
  // not one per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(globalFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [globalFilter]);

  // Any change to the result set returns to page 1.
  useEffect(() => {
    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  }, [debouncedSearch, columnFilters, sorting]);

  // ---------- server-driven data fetch ----------
  // Server-side pagination, filtering and sorting. Previously the grid pulled
  // EVERY enrolled asset and did all three in the browser, so a 100k-asset
  // project meant 100 sequential requests and 100k rows held in JS just to
  // show 100 of them. Now exactly one page is fetched, and the search box,
  // filter chips and sort headers are translated into query parameters.
  const serverParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set('include', 'details');
    p.set('limit', String(pagination.pageSize));
    p.set('offset', String(pagination.pageIndex * pagination.pageSize));
    if (debouncedSearch) p.set('search', debouncedSearch);
    for (const [colId, param] of [
      ['asset_type_name', 'asset_type_name'],
      ['system_name', 'system_name'],
      ['business_unit_name', 'business_unit_name'],
      ['business_criticality', 'business_criticality'],
      ['vendor_supplied_asset', 'vendor_supplied_asset'],
    ] as Array<[string, string]>) {
      const f = columnFilters.find((c) => c.id === colId);
      if (f?.value) p.set(param, String(f.value));
    }
    const s = sorting[0];
    if (s) {
      p.set('sort_by', s.id);
      p.set('sort_dir', s.desc ? 'desc' : 'asc');
    }
    return p.toString();
  }, [pagination.pageSize, pagination.pageIndex, debouncedSearch, columnFilters, sorting]);

  const { data: page, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['projects', projectId, 'assets', 'detailed', serverParams],
    queryFn: async () => {
      const result = await getWithMeta<ProjectAssetDetail[]>(
        `/projects/${projectId}/assets?${serverParams}`,
      );
      const meta = result.meta as unknown as PaginatedMeta & { total?: number | null };
      return { rows: result.data, total: meta.total ?? result.data.length };
    },
    // Keep the previous page on screen while the next one loads so the grid
    // doesn't flash empty on every page/sort/filter change.
    placeholderData: (prev) => prev,
  });

  const rows = page?.rows ?? [];
  const totalRows = page?.total ?? 0;

  // Filter dropdown options come from the server: with only one page loaded,
  // deriving them from `rows` would list just this page's values.
  const { data: facets } = useQuery({
    queryKey: ['projects', projectId, 'asset-facets'],
    queryFn: () => api.get<{ types: string[]; systems: string[]; businessUnits: string[] }>(
      `/projects/${projectId}/assets/facets`,
    ),
    staleTime: 5 * 60 * 1000,
  });

  // ---------- remove from project ----------
  const removeFromProject = useMutation({
    mutationFn: (assetIds: string[]) => api.post<{ removed: number }>(`/projects/${projectId}/assets/remove`, { asset_ids: assetIds }),
    onSuccess: () => {
      setRowSelection({});
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'assets'] });
    },
  });

  // ---------- bulk update mutation ----------
  const bulkUpdate = useMutation({
    mutationFn: async (updates: Array<{ id: string; patch: Record<string, unknown> }>) => {
      return api.patch<BulkUpdateResult>('/assets/bulk', { updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'assets'] });
    },
  });


  // Slice 6c: hover-to-illuminate. When the user hovers over a module
  // score column header, the AssetGrid columns that feed that module's
  // score get a subtle highlight. State lives on the grid so that
  // column cells can read it via column meta.
  const [hoveredModule, setHoveredModule] = useState<ModuleCode | null>(null);

  // Slice 7.7: per-asset threat composition selection panel. Opens when the
  // user clicks the Threat cell of any row. Cell renders a hint dot when
  // status='Defaults' so unreviewed assets are visible at a glance.
  const [threatPanelAssetId, setThreatPanelAssetId] = useState<string | null>(null);

  // Slice 7.9: persistent view state — collapsed groups, hidden individual
  // columns, view preset (standard/compact/detailed). All persisted to
  // localStorage so user choice survives sessions.
  const viewState = useGridViewState();

  // ---------- column definitions ----------
  // Slice 6c: columns are organized into a two-tier hierarchy via
  // TanStack's group() construct. Three groups:
  //
  //   Asset           — identity (name, type, system, BU, owner)
  //   Properties      — editable asset attributes (exposure, criticality, vendor)
  //   Foundation Risk Analysis — the four Foundation modules
  //   Integrated Foundation    — the composite aggregator (sits visually
  //                              apart so the eye reads inputs → output)
  //
  // Each leaf column carries `meta.feedsModules`, listing which modules'
  // scores this column's value contributes to. When the user hovers a
  // module score column header, the grid highlights all leaf columns
  // whose `feedsModules` include that module.
  //
  // Module score columns also carry `meta.moduleCode`, which is what
  // the hover handler watches for.
  const columns = useMemo<ColumnDef<ProjectAssetDetail>[]>(() => {
    // Helper: build a column-meta `feedsModules` list by scanning
    // MODULE_INPUTS for any module whose visibleColumns include this
    // accessor key. So adding a new module input mapping in
    // module-inputs.ts automatically lights up the right columns.
    const feedsFor = (accessor: string): ModuleCode[] => {
      const out: ModuleCode[] = [];
      for (const [code, inputs] of Object.entries(MODULE_INPUTS) as Array<[
        ModuleCode, typeof MODULE_INPUTS[ModuleCode],
      ]>) {
        if (inputs.visibleColumns.includes(accessor)) out.push(code);
      }
      return out;
    };

    const selectColumn: ColumnDef<ProjectAssetDetail> = {
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          aria-label="Select all"
          className="h-3.5 w-3.5 cursor-pointer"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          aria-label="Select row"
          className="h-3.5 w-3.5 cursor-pointer"
        />
      ),
      size: 32,
      enableSorting: false,
    };

    const assetGroup: ColumnDef<ProjectAssetDetail> = {
      id: 'group_asset',
      header: 'Asset',
      columns: [
        {
          accessorKey: 'asset_name',
          header: 'Name',
          cell: ({ row }) => (
            <Link
              to={`/projects/${projectId}/assets/${row.original.asset_id}`}
              className="font-medium text-ink-900 hover:text-accent hover:underline"
              title={row.original.asset_name}
            >
              {row.original.asset_name}
            </Link>
          ),
          size: 280,
          minSize: 240,  // never shrink below this — long names like
                         // "Transaction history warehouse" need ~210px at 13px font
          meta: { feedsModules: feedsFor('asset_name') },
        },
        {
          accessorKey: 'asset_type_name',
          header: 'Type',
          cell: ({ getValue }) => (
            <span className="text-xs text-ink-500">{getValue<string>()}</span>
          ),
          size: 120,
          filterFn: 'equalsString',
          meta: { feedsModules: feedsFor('asset_type_name') },
        },
        {
          accessorKey: 'system_name',
          header: 'System',
          cell: ({ getValue }) => (
            <span className="text-xs text-ink-500">{getValue<string>()}</span>
          ),
          size: 130,
          filterFn: 'equalsString',
          meta: { feedsModules: feedsFor('system_name') },
        },
        {
          accessorKey: 'business_unit_name',
          header: 'BU',
          cell: ({ getValue }) => (
            <span className="text-xs text-ink-500">{getValue<string>()}</span>
          ),
          size: 110,
          filterFn: 'equalsString',
          meta: { feedsModules: feedsFor('business_unit_name') },
        },
        {
          accessorKey: 'owner_role_text',
          header: 'Owner',
          cell: ({ getValue }) => {
            const v = getValue<string | null>();
            return (
              <span className="text-xs text-ink-500">
                {v ?? <span className="text-ink-300">Unassigned</span>}
              </span>
            );
          },
          size: 140,
          meta: { feedsModules: feedsFor('owner_role_text') },
        },
      ],
    };

    const propertiesGroup: ColumnDef<ProjectAssetDetail> = {
      id: 'group_properties',
      header: 'Properties',
      columns: [
        {
          accessorKey: 'external_exposure',
          header: 'Exposure',
          cell: ({ getValue }) => {
            const v = getValue<string>();
            const cls = v === 'Internet_facing'
              ? 'bg-risk-high/10 text-risk-high'
              : v === 'Partner'
              ? 'bg-risk-moderate/10 text-risk-moderate'
              : 'bg-ink-100 text-ink-700';
            return (
              <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-medium uppercase tracking-wide ${cls}`}>
                {v.replace('_', ' ')}
              </span>
            );
          },
          size: 100,
          filterFn: 'equalsString',
          meta: { feedsModules: feedsFor('external_exposure') },
        },
        {
          // Unified platform contract column (Asset Unification): the
          // uppercase cross-product status. lifecycle_status remains the
          // QRAM-native lifecycle; this surfaces the shared contract value.
          accessorKey: 'status',
          header: 'Status',
          cell: ({ getValue }) => {
            const v = getValue<string | undefined>();
            if (!v) return <span className="text-ink-300 text-xs">—</span>;
            const cls = v === 'RETIRED' || v === 'DEPRECATED'
              ? 'bg-ink-100 text-ink-500'
              : v === 'ACTIVE'
              ? 'bg-risk-low/10 text-risk-low'
              : 'bg-ink-100 text-ink-700';
            return (
              <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-medium uppercase tracking-wide ${cls}`}>
                {v}
              </span>
            );
          },
          size: 100,
          filterFn: 'equalsString',
        },
        {
          // Unified platform contract column (Asset Unification).
          accessorKey: 'environment',
          header: 'Environment',
          cell: ({ getValue }) => {
            const v = getValue<string | undefined>();
            if (!v) return <span className="text-ink-300 text-xs">—</span>;
            return (
              <span className="px-1.5 py-0.5 text-[10px] rounded-sm font-medium uppercase tracking-wide bg-ink-100 text-ink-700">
                {v}
              </span>
            );
          },
          size: 110,
          filterFn: 'equalsString',
        },
        {
          accessorKey: 'business_criticality',
          header: 'Criticality',
          cell: ({ row, getValue }) => {
            const v = getValue<LikertLevel | null>();
            return (
              <InlineLikertEdit
                value={v}
                onChange={(next) => {
                  bulkUpdate.mutate([{
                    id: row.original.asset_id,
                    patch: { business_criticality: next },
                  }]);
                }}
                disabled={bulkUpdate.isPending}
              />
            );
          },
          size: 110,
          filterFn: (row, _id, filterValue) => {
            if (!filterValue) return true;
            return row.original.business_criticality === filterValue;
          },
          meta: { feedsModules: feedsFor('business_criticality') },
        },
        {
          accessorKey: 'vendor_supplied_asset',
          header: 'Vendor',
          cell: ({ getValue }) => {
            const v = getValue<string | null>();
            if (v === null) return <span className="text-ink-300 text-xs">—</span>;
            return (
              <span className={`text-xs ${v === 'Yes' ? 'text-ink-700' : 'text-ink-500'}`}>
                {v}
              </span>
            );
          },
          size: 70,
          filterFn: 'equalsString',
          meta: { feedsModules: feedsFor('vendor_supplied_asset') },
        },
      ],
    };

    // Module score columns. Foundation gets its 4 inputs + integrated
    // summary; Advanced gets Threat (Slice 7) + Crypto/Compliance/integrated
    // advanced (Slice 8+).

    const moduleScoreColumn = (mod: ModuleCode): ColumnDef<ProjectAssetDetail> => ({
      id: `risk_${mod}`,
      header: moduleLabel(mod),
      accessorFn: (row) => row.latest_risk_by_module?.[mod] ?? null,
      cell: ({ row }) => {
        const r = row.original.latest_risk_by_module?.[mod] ?? null;
        // Slice 7.7: Threat cell is clickable + shows a hint when the asset
        // is on default selections. Other module cells stay read-only badges.
        if (mod === 'threat') {
          const isDefaults = row.original.threat_selection_status === 'Defaults';
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setThreatPanelAssetId(row.original.asset_id);
              }}
              className="text-left w-full hover:bg-ink-50 -mx-2 px-2 py-1 rounded-sm flex items-center gap-1.5 group"
              title="Click to review threat compositions for this asset"
            >
              {r === null ? (
                <span className="text-ink-300 text-[10px]">—</span>
              ) : (
                <RiskBadge level={r.risk_level} showScore={r.normalized_score} />
              )}
              {isDefaults && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"
                  title="Threat selections not yet reviewed (using auto-applied defaults)"
                />
              )}
              <span className="text-[10px] text-ink-400 group-hover:text-accent ml-auto">
                edit
              </span>
            </button>
          );
        }
        if (r === null) return <span className="text-ink-300 text-[10px]">—</span>;
        return <RiskBadge level={r.risk_level} showScore={r.normalized_score} />;
      },
      size: mod === 'threat' ? 220 : 130,
      sortingFn: (a, b) => {
        const ra = a.original.latest_risk_by_module?.[mod]?.risk_level ?? null;
        const rb = b.original.latest_risk_by_module?.[mod]?.risk_level ?? null;
        return (ra ? RISK_RANK[ra] : -1) - (rb ? RISK_RANK[rb] : -1);
      },
      meta: { moduleCode: mod, feedsModules: feedsFor(`risk_${mod}`) },
    });

    // Slice 7.9: column groups now match the visual hierarchy users see in
    // the toolbar's collapse controls and Columns dropdown. Foundation owns
    // its 4 input modules + integrated_foundation as the summary. Advanced
    // owns Threat (today) + Cryptography/Compliance/integrated_advanced
    // (Slice 8+).
    const FOUNDATION_INPUTS: ModuleCode[] = [
      'timeline', 'data_risk', 'supply_chain', 'migration_feasibility',
    ];
    const ADVANCED_INPUTS: ModuleCode[] = ['threat', 'cryptography', 'compliance'];

    const foundationInputCols = FOUNDATION_INPUTS
      .filter((m) => moduleScope.includes(m))
      .map(moduleScoreColumn);
    const foundationSummaryCol = moduleScope.includes('integrated_foundation')
      ? moduleScoreColumn('integrated_foundation')
      : null;
    const advancedInputCols = ADVANCED_INPUTS
      .filter((m) => moduleScope.includes(m))
      .map(moduleScoreColumn);
    const advancedSummaryCol = moduleScope.includes('integrated_advanced')
      ? moduleScoreColumn('integrated_advanced')
      : null;

    const cols: ColumnDef<ProjectAssetDetail>[] = [
      selectColumn,
      assetGroup,
      propertiesGroup,
    ];

    if (foundationInputCols.length > 0 || foundationSummaryCol) {
      const groupCols: ColumnDef<ProjectAssetDetail>[] = [...foundationInputCols];
      if (foundationSummaryCol) groupCols.push(foundationSummaryCol);
      cols.push({
        id: 'group_foundation',
        header: GROUP_LABEL.group_foundation,
        columns: groupCols,
      });
    }
    if (advancedInputCols.length > 0 || advancedSummaryCol) {
      const groupCols: ColumnDef<ProjectAssetDetail>[] = [...advancedInputCols];
      if (advancedSummaryCol) groupCols.push(advancedSummaryCol);
      cols.push({
        id: 'group_advanced',
        header: GROUP_LABEL.group_advanced,
        columns: groupCols,
      });
    }

    return cols;
  }, [moduleScope, projectId, bulkUpdate]);

  // Slice 7.9: compute the columnVisibility map TanStack consumes.
  //
  // Two inputs from viewState drive visibility:
  //   - collapsedGroups: when a group is collapsed, all its leaf columns
  //     hide EXCEPT the group's summary (the integrated_* score).
  //   - hiddenColumns: explicit hide via the Columns dropdown wins
  //     regardless of group state.
  //
  // Mapping: group_foundation collapses → all foundation-input modules hide,
  // integrated_foundation stays. group_advanced collapses → threat hides
  // (and Slice-8 modules will hide too), integrated_advanced stays. The
  // identity columns (asset_name, type, system, BU, owner) are unaffected
  // by either group's state — they live in `assetGroup` which has
  // alwaysVisible=true conceptually.
  const columnVisibility = useMemo(() => {
    const map: Record<string, boolean> = {};
    const groupOfModule: Record<string, string> = {
      timeline: 'group_foundation',
      data_risk: 'group_foundation',
      supply_chain: 'group_foundation',
      migration_feasibility: 'group_foundation',
      integrated_foundation: 'group_foundation',
      threat: 'group_advanced',
      cryptography: 'group_advanced',
      compliance: 'group_advanced',
      integrated_advanced: 'group_advanced',
    };
    // Each group's "summary" module — the one that stays visible when the
    // group is collapsed. Prefer the integrated_* module; if it's not in
    // moduleScope (like DemoBank's Slice-7-era projects, which have
    // integrated_foundation but no integrated_advanced), fall back to the
    // first available input module so the group never collapses to zero
    // visible columns. Otherwise the user sees a phantom group header
    // floating over nothing.
    const preferredSummary: Record<string, string> = {
      group_foundation: 'integrated_foundation',
      group_advanced: 'integrated_advanced',
    };
    const summaryOfGroup: Record<string, string | undefined> = {};
    for (const groupId of ['group_foundation', 'group_advanced']) {
      const preferred = preferredSummary[groupId];
      if (preferred && moduleScope.includes(preferred as ModuleCode)) {
        summaryOfGroup[groupId] = preferred;
      } else {
        // Pick first module in this group from moduleScope as fallback summary
        const fallback = moduleScope.find((m) => groupOfModule[m] === groupId);
        if (fallback) summaryOfGroup[groupId] = fallback;
      }
    }
    for (const mod of moduleScope) {
      const groupId = groupOfModule[mod];
      if (!groupId) continue;
      const colId = `risk_${mod}`;
      const isSummary = summaryOfGroup[groupId] === mod;
      const hidden = isColumnHidden({
        columnId: colId,
        groupId,
        isGroupSummary: isSummary,
        hiddenColumns: viewState.hiddenColumns,
        collapsedGroups: viewState.collapsedGroups,
      });
      map[colId] = !hidden;
    }
    // Properties group: Exposure + Vendor hide when collapsed;
    // Criticality is the summary and always remains visible.
    const propertiesCollapsed = !!viewState.collapsedGroups['group_properties'];
    map['external_exposure'] = propertiesCollapsed
      ? false
      : !viewState.hiddenColumns['external_exposure'];
    map['business_criticality'] = !viewState.hiddenColumns['business_criticality'];
    map['vendor_supplied_asset'] = propertiesCollapsed
      ? false
      : !viewState.hiddenColumns['vendor_supplied_asset'];
    // Identity columns can be toggled individually but never collapsed
    for (const colId of ['asset_name', 'asset_type_name', 'system_name', 'business_unit_name', 'owner_role_text']) {
      const hidden = !!viewState.hiddenColumns[colId];
      map[colId] = !hidden;
    }
    return map;
  }, [moduleScope, viewState.hiddenColumns, viewState.collapsedGroups]);

  // ---------- table instance ----------
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnFilters, rowSelection, globalFilter, columnVisibility, pagination },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getRowId: (row) => row.asset_id,
    getCoreRowModel: getCoreRowModel(),
    // Sorting, filtering and pagination all happen in SQL now, so the
    // corresponding client row models are switched off. Feeding TanStack the
    // server's page plus rowCount keeps getPageCount()/header sort indicators
    // working while it never re-sorts or re-filters the data itself.
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalRows,
    // Don't auto-reset the page on every data change — an inline edit refetches
    // the list, and we don't want that to bounce the user back to page 1. We
    // reset the page explicitly when the filter/search/sort changes instead.
    autoResetPageIndex: false,
    enableRowSelection: true,
    globalFilterFn: (row, _id, filter) => {
      const lf = filter.toLowerCase();
      const r = row.original;
      return (
        r.asset_name.toLowerCase().includes(lf) ||
        r.asset_type_name.toLowerCase().includes(lf) ||
        r.system_name.toLowerCase().includes(lf) ||
        r.business_unit_name.toLowerCase().includes(lf) ||
        (r.owner_role_text?.toLowerCase().includes(lf) ?? false) ||
        (r.asset_external_ref?.toLowerCase().includes(lf) ?? false)
      );
    },
  });

  // With manual pagination the core row model IS the current page — the server
  // already applied the filters and ordering.
  const pageRows = table.getRowModel().rows;
  const selectedRows = table.getSelectedRowModel().rows;
  const pageCount = table.getPageCount();

  // ---------- virtualization ----------
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: pageRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 12,
  });

  // Jump back to the top of the list whenever the page changes so a new page
  // doesn't start mid-scroll.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [pagination.pageIndex, pagination.pageSize]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0]!.start : 0;
  const paddingBottom = virtualItems.length > 0
    ? totalSize - virtualItems[virtualItems.length - 1]!.end
    : 0;

  // ---------- distinct values for filter chips ----------
  // Server-provided (see the facets query above): with one page loaded, the
  // options can no longer be derived from the rows in hand.
  const distinctValues = useMemo(() => ({
    types: facets?.types ?? [],
    systems: facets?.systems ?? [],
    bus: facets?.businessUnits ?? [],
  }), [facets]);

  // ---------- CSV export ----------
  // Built on the server (GET /projects/:id/assets/results.csv) over every row matching the current
  // filters, not just the page on screen, with the latest run's broken-today items, cost band and
  // range, and vendor gate. Paging is not sent: the export covers the full filtered set.
  const [exportProgress, setExportProgress] = useState<string | null>(null);
  const exportCsv = useCallback(async () => {
    if (exportProgress) return;
    try {
      setExportProgress('…');
      const p = new URLSearchParams(serverParams);
      p.delete('include');
      p.delete('limit');
      p.delete('offset');
      if (moduleScope.length) p.set('modules', moduleScope.join(','));
      const res = await fetch(apiUrl(`/projects/${projectId}/assets/results.csv?${p.toString()}`), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.download = `qram-assets-${projectId.slice(0, 8)}-${stamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Asset CSV export failed', err);
      window.alert('The export failed and nothing was downloaded. Please try again.');
    } finally {
      setExportProgress(null);
    }
  }, [serverParams, moduleScope, projectId, exportProgress]);

  // Round-trippable export: downloads a CSV from the backend shaped
  // EXACTLY like the asset import template (same columns, same order),
  // with the CURRENT values of every asset enrolled in this project.
  // Unlike exportCsv above, it ignores grid filters and pagination —
  // the point is a file that can be edited offline and re-imported in
  // Upsert mode (rows matched back to assets by external_ref).
  // Server-side because grid rows don't carry every template field
  // (shelf life, upgradability, dates, is_confidential, …).
  const [importFormatExporting, setImportFormatExporting] = useState(false);
  const exportImportFormatCsv = useCallback(async () => {
    setImportFormatExporting(true);
    try {
      const res = await fetch(
        apiUrl(`/projects/${projectId}/assets/export/import-format`),
        { credentials: 'same-origin' },
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      link.download = `qram-assets-import-format-${projectId.slice(0, 8)}-${stamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setImportFormatExporting(false);
    }
  }, [projectId]);

  // ---------- bulk operations ----------
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);

  // Close the bulk panel automatically when selection clears
  useEffect(() => {
    if (selectedRows.length === 0) setBulkPanelOpen(false);
  }, [selectedRows.length]);

  if (isLoading) {
    return <div className="p-8 text-ink-500 text-sm">Loading assets…</div>;
  }
  if (isError) {
    return (
      <div className="p-8 text-accent text-sm">
        Failed to load assets: {(error as Error)?.message ?? 'unknown error'}
      </div>
    );
  }
  // With server-side filtering, "no rows" has two very different meanings.
  // Only the genuinely-empty project gets the onboarding empty state; an
  // over-narrow filter must keep the toolbar mounted, otherwise the controls
  // needed to widen the filter disappear along with the rows and it looks like
  // the data was lost.
  const hasActiveFilters = !!debouncedSearch || columnFilters.length > 0;

  if (rows.length === 0 && !hasActiveFilters) {
    return (
      <div className="p-12 text-center">
        <div className="text-ink-300 text-sm mb-4">No assets enrolled yet.</div>
        <Link
          to={`/projects/${projectId}/assets/new`}
          className="btn-primary inline-block"
        >
          Add first asset
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter / search bar */}
      <div className="border-b border-ink-100 px-4 py-3 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search name, owner, system…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="w-72 text-sm"
        />
        <FilterChip
          label="Type"
          values={distinctValues.types}
          active={(table.getColumn('asset_type_name')?.getFilterValue() as string) ?? null}
          onChange={(v) => table.getColumn('asset_type_name')?.setFilterValue(v ?? undefined)}
        />
        <FilterChip
          label="System"
          values={distinctValues.systems}
          active={(table.getColumn('system_name')?.getFilterValue() as string) ?? null}
          onChange={(v) => table.getColumn('system_name')?.setFilterValue(v ?? undefined)}
        />
        <FilterChip
          label="BU"
          values={distinctValues.bus}
          active={(table.getColumn('business_unit_name')?.getFilterValue() as string) ?? null}
          onChange={(v) => table.getColumn('business_unit_name')?.setFilterValue(v ?? undefined)}
        />
        <FilterChip
          label="Criticality"
          values={LIKERT_LEVELS}
          active={(table.getColumn('business_criticality')?.getFilterValue() as string) ?? null}
          onChange={(v) => table.getColumn('business_criticality')?.setFilterValue(v ?? undefined)}
        />
        <FilterChip
          label="Vendor"
          values={['Yes', 'No']}
          active={(table.getColumn('vendor_supplied_asset')?.getFilterValue() as string) ?? null}
          onChange={(v) => table.getColumn('vendor_supplied_asset')?.setFilterValue(v ?? undefined)}
        />

        {/* Slice 7.9: view-state controls */}
        <ViewPresetSelect
          value={viewState.view}
          onChange={viewState.setView}
        />
        <ColumnsDropdown
          moduleScope={moduleScope}
          collapsedGroups={viewState.collapsedGroups}
          hiddenColumns={viewState.hiddenColumns}
          toggleGroup={viewState.toggleGroup}
          setColumnVisibility={viewState.setColumnVisibility}
          resetToDefaults={viewState.resetToDefaults}
        />

        <div className="flex-grow" />

        <span className="text-xs text-ink-500 font-mono tabular-nums">
          {totalRows.toLocaleString()} matching
        </span>
      </div>

      {/* Actions + pagination row. Exports sit left, the pager is right-aligned
          directly under the row count — keeping both on ONE line rather than
          giving the pager a row of its own, which cost ~50px of table height. */}
      <div className="border-b border-ink-100 px-4 py-2 flex items-center gap-3 flex-wrap">
        <button
          onClick={exportCsv}
          disabled={!!exportProgress}
          className="btn-secondary text-xs py-1 px-3 disabled:opacity-60"
        >
          {exportProgress ? 'Exporting…' : 'Export CSV'}
        </button>
        <button
          onClick={exportImportFormatCsv}
          disabled={importFormatExporting}
          className="btn-secondary text-xs py-1 px-3"
          title="Download all enrolled assets as a CSV matching the import template — edit offline and re-import (Upsert)."
        >
          {importFormatExporting ? 'Exporting…' : 'Export (import format)'}
        </button>

        <div className="flex-grow" />

        {totalRows > 0 && (
          <Pagination
            pageIndex={pagination.pageIndex}
            pageCount={pageCount}
            onPageChange={(i) => table.setPageIndex(i)}
            pageSize={pagination.pageSize}
            onPageSizeChange={(s) => table.setPageSize(s)}
            totalItems={totalRows}
            disabled={isFetching}
            itemLabel="assets"
            className="justify-end"
          />
        )}
      </div>

      {/* Bulk actions bar */}
      {selectedRows.length > 0 && (
        <div className="bg-accent text-white px-4 py-2 flex items-center gap-3 text-sm">
          <span className="font-medium">{selectedRows.length} selected</span>
          <button
            onClick={() => setBulkPanelOpen((v) => !v)}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-sm text-xs"
          >
            Bulk edit
          </button>
          <button
            onClick={() => {
              const ids = selectedRows.map((r) => r.original.asset_id);
              if (window.confirm(`Remove ${ids.length} asset${ids.length === 1 ? '' : 's'} from this project? Their scan data and settings stay with the asset; the next run no longer assesses them.`)) removeFromProject.mutate(ids);
            }}
            disabled={removeFromProject.isPending}
            data-testid="bulk-remove"
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-sm text-xs disabled:opacity-50"
          >
            Remove from project
          </button>
          <button
            onClick={() => setRowSelection({})}
            className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-sm text-xs"
          >
            Clear
          </button>
          <div className="flex-grow" />
          {removeFromProject.isError && <span className="text-xs" role="alert">{removeFromProject.error instanceof Error ? removeFromProject.error.message : 'Remove failed'}</span>}
          {(bulkUpdate.isPending || removeFromProject.isPending) && <span className="text-xs">Saving…</span>}
        </div>
      )}

      {bulkPanelOpen && (
        <BulkEditPanel
          selectedIds={selectedRows.map((r) => r.original.asset_id)}
          onApply={(patch) => {
            const updates = selectedRows.map((r) => ({
              id: r.original.asset_id,
              patch,
            }));
            bulkUpdate.mutate(updates);
            setBulkPanelOpen(false);
          }}
          onCancel={() => setBulkPanelOpen(false)}
        />
      )}

      {rows.length === 0 && (
        <div className="p-10 text-center">
          <div className="text-ink-500 text-sm mb-1">No assets match these filters.</div>
          <div className="text-ink-300 text-xs mb-4">
            {totalRows === 0
              ? 'Try clearing the search or a filter chip.'
              : 'This page is empty — jump back to page 1.'}
          </div>
          <button
            type="button"
            onClick={() => {
              setGlobalFilter('');
              setColumnFilters([]);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            className="btn-secondary text-xs py-1 px-3"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Table */}
      {/*
        Slice 7.9: horizontal scroll affordances. When the user expands all
        groups (or has a narrow window), the table is wider than the
        viewport. Native bottom-scrollbars on long tables are easy to miss
        — clickable chevron handles on the left/right give the user an
        always-reachable way to scroll horizontally without scrolling
        vertically first.
      */}
      <ScrollShellWithHandles scrollRef={scrollRef} className="flex-grow">
        {/*
          Slice 7.9-α5: explicit minWidth based on the sum of TanStack
          column sizes. Previously the table had `w-full` + tableLayout
          'fixed', which meant the table compressed to fit whatever
          container width was available — so column sizes (32-240px)
          became approximations and the rightmost cells (Threat badge +
          edit link + amber dot) got clipped by their compressed `<th>`
          widths, not by any container overflow. Result: no scrollbar,
          just silently clipped content.

          With minWidth set to the sum of column sizes (~1900px when
          all groups expanded), the table now overflows when the
          viewport is narrower, the inner div's `overflow-auto` engages,
          and the chevron handles fire. The padding-right approach in
          α4 didn't fix this — it shrank the container and made
          compression worse.
        */}
        <table
          className="text-sm"
          style={{
            tableLayout: 'fixed',
            // Width is the sum of column sizes. We deliberately do NOT
            // set width: 100% here — that would let the browser redistribute
            // widths proportionally when the viewport is wider than the
            // table's natural size, shrinking individual columns below
            // their requested `size`. The asset name column then ends up
            // ~100px wide (truncating "Customer master DB" → "Customer
            // mas...") even though it requested 280px.
            //
            // With just minWidth set: when viewport > minWidth, the table
            // fills its natural size and there's empty space on the right;
            // when viewport < minWidth, the inner overflow-auto engages
            // and the chevron handles fire. Either way every column gets
            // exactly the size it asked for.
            minWidth: `${table.getTotalSize()}px`,
          }}
        >
          <thead className="bg-ink-50/80 backdrop-blur sticky top-0 z-10 border-b border-ink-200">
            {table.getHeaderGroups().map((hg, hgIndex) => {
              // hgIndex 0 is the top-tier (group) row; the last is the leaf row.
              const isGroupRow = hgIndex < table.getHeaderGroups().length - 1;
              return (
                <tr key={hg.id} className={isGroupRow ? 'border-b border-ink-100' : ''}>
                  {hg.headers.map((header) => {
                    const meta = header.column.columnDef.meta;
                    const moduleCode = meta?.moduleCode;
                    const isGroupHeader = header.subHeaders.length > 0;

                    // Group header — different visual treatment
                    if (isGroupHeader) {
                      // Slice 7.9-α3: TanStack auto-generates header.id from
                      // position/colspan (e.g. "0_5_0"). The column-definition
                      // id we set ('group_foundation', 'group_advanced') lives
                      // on header.column.id. Using header.id here was the
                      // root cause of the missing chevrons in α1 and α2 —
                      // every check fell through to the default styling
                      // branch, no button wrapper, no chevron. Confirmed via
                      // DevTools rendered HTML.
                      const groupId = header.column.id;
                      // Slice 7.9: which groups are collapsible? group_foundation
                      // and group_advanced. Identity/Properties/etc. are not.
                      const isCollapsible = groupId === 'group_foundation' || groupId === 'group_advanced' || groupId === 'group_properties';
                      const isCollapsed = !!viewState.collapsedGroups[groupId];
                      const cls =
                        groupId === 'group_foundation'
                          ? 'bg-accent/5 text-accent border-l border-r border-accent/20'
                          : groupId === 'group_advanced'
                          ? 'bg-amber-50 text-amber-900 border-l border-r border-amber-200'
                          : groupId === 'group_properties'
                          ? 'bg-ink-50 text-ink-600 border-l border-r border-ink-200'
                          : 'text-ink-400';
                      const labelEl = (
                        <span className="inline-flex items-center justify-center gap-1.5">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {isCollapsible && (
                            <span
                              className="text-xs font-bold leading-none"
                              style={{ fontSize: '11px' }}
                              aria-hidden="true"
                            >
                              {isCollapsed ? '▶' : '▼'}
                            </span>
                          )}
                        </span>
                      );
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className={`text-center text-[10px] font-medium uppercase tracking-widest py-1.5 ${cls}`}
                        >
                          {isCollapsible ? (
                            <button
                              type="button"
                              onClick={() => viewState.toggleGroup(groupId)}
                              className="hover:bg-black/5 cursor-pointer w-full py-0.5 px-2 rounded-sm transition-colors"
                              title={isCollapsed
                                ? `Click to expand ${GROUP_LABEL[groupId] ?? 'Properties'} columns`
                                : `Click to collapse ${GROUP_LABEL[groupId] ?? 'Properties'} columns`}
                            >
                              {labelEl}
                            </button>
                          ) : (
                            labelEl
                          )}
                        </th>
                      );
                    }

                    // Placeholder for spanning columns (e.g. select col under tier 0)
                    if (header.isPlaceholder) {
                      return <th key={header.id} style={{ width: header.getSize() }} />;
                    }

                    // Leaf header — sortable, optionally module-score
                    return (
                      <th
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2"
                        onMouseEnter={moduleCode ? () => setHoveredModule(moduleCode) : undefined}
                        onMouseLeave={moduleCode ? () => setHoveredModule(null) : undefined}
                      >
                        <button
                          onClick={header.column.getToggleSortingHandler()}
                          disabled={!header.column.getCanSort()}
                          className={`flex items-center gap-1 hover:text-ink-900 disabled:cursor-default disabled:hover:text-ink-500 ${
                            moduleCode ? 'cursor-help' : ''
                          }`}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: '▲',
                            desc: '▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </button>
                        {moduleCode && hoveredModule === moduleCode && (
                          <ModuleInputsPopover module={moduleCode} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr><td colSpan={table.getVisibleLeafColumns().length} style={{ height: paddingTop }} /></tr>
            )}
            {virtualItems.map((vItem) => {
              const row = pageRows[vItem.index]!;
              return (
                <tr
                  key={row.id}
                  className={`border-b border-ink-100 hover:bg-ink-50/50 ${row.getIsSelected() ? 'bg-accent/5' : ''}`}
                  style={{ height: 36 }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    const isHighlighted =
                      hoveredModule !== null &&
                      meta?.feedsModules?.includes(hoveredModule);
                    return (
                      <td
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                        className={`px-3 py-1.5 truncate transition-colors duration-100 ${
                          isHighlighted ? 'bg-accent/10' : ''
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr><td colSpan={table.getVisibleLeafColumns().length} style={{ height: paddingBottom }} /></tr>
            )}
          </tbody>
        </table>
      </ScrollShellWithHandles>

      {/* Slice 7.7: per-asset threat composition selection panel.
          Opens when the user clicks the Threat cell of any row. */}
      <ThreatSelectionPanel
        open={threatPanelAssetId !== null}
        onClose={() => setThreatPanelAssetId(null)}
        assetId={threatPanelAssetId}
        projectId={projectId}
      />
    </div>
  );
}

// =============================================================================
// Inline edit cell — the criticality column is editable in-place. Other
// inline-editable columns can use the same component.
// =============================================================================

interface InlineLikertEditProps {
  value: LikertLevel | null;
  onChange: (next: LikertLevel) => void;
  disabled?: boolean;
}

function InlineLikertEdit({ value, onChange, disabled }: InlineLikertEditProps) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <select
        autoFocus
        value={value ?? ''}
        onBlur={() => setEditing(false)}
        onChange={(e) => {
          onChange(e.target.value as LikertLevel);
          setEditing(false);
        }}
        disabled={disabled}
        className="text-xs h-6 py-0 px-1"
      >
        {LIKERT_LEVELS.map((l) => (
          <option key={l} value={l}>{l.replace('_', ' ')}</option>
        ))}
      </select>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className="text-xs text-ink-700 hover:text-accent hover:underline text-left"
    >
      {value ? value.replace('_', ' ') : <span className="text-ink-300">— set</span>}
    </button>
  );
}

// =============================================================================
// Filter chip — single-select dropdown for a categorical column
// =============================================================================

interface FilterChipProps {
  label: string;
  values: string[];
  active: string | null;
  onChange: (value: string | null) => void;
}

function FilterChip({ label, values, active, onChange }: FilterChipProps) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-ink-500">{label}:</span>
      <select
        value={active ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="text-xs h-7 py-0 px-1.5 min-w-[80px]"
      >
        <option value="">All</option>
        {values.map((v) => (
          <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>
        ))}
      </select>
    </div>
  );
}

// =============================================================================
// Slice 7.9: ViewPresetSelect
//
// Dropdown that swaps the AssetGrid between Standard, Compact, and Detailed
// views. Compact = collapse all groups (max scanning density). Detailed =
// expand all groups (max information density). Standard = sensible defaults
// (Foundation expanded, Advanced collapsed).
// =============================================================================

interface ViewPresetSelectProps {
  value: ViewPreset;
  onChange: (v: ViewPreset) => void;
}

function ViewPresetSelect({ value, onChange }: ViewPresetSelectProps) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-ink-500">View:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ViewPreset)}
        className="text-xs h-7 py-0 px-1.5 min-w-[100px]"
      >
        <option value="standard">Standard</option>
        <option value="compact">Compact</option>
        <option value="detailed">Detailed</option>
      </select>
    </div>
  );
}

// =============================================================================
// Slice 7.9: ColumnsDropdown
//
// Click-popover that lets the user toggle individual columns and groups.
// Dismissed on outside click or escape. Hidden columns are stored in the
// view-state hook (localStorage-backed) so the choice persists.
//
// Layout:
//   ┌─ Columns ▼
//   │  ─ Identity ───────────
//   │    ☑ Asset name (always)
//   │    ☑ Type
//   │    ...
//   │  ─ Foundation [Expand/Collapse] ─
//   │    ☑ Timeline
//   │    ☑ Data Risk
//   │    ...
//   │  ─ Advanced [Expand/Collapse] ─
//   │    ☑ Threat
//   └  [Reset to defaults]
//
// "Always" badges mark columns the user can't hide (Asset name).
// =============================================================================

interface ColumnsDropdownProps {
  moduleScope: ModuleCode[];
  collapsedGroups: Record<string, boolean>;
  hiddenColumns: Record<string, boolean>;
  toggleGroup: (groupId: string) => void;
  setColumnVisibility: (columnId: string, visible: boolean) => void;
  resetToDefaults: () => void;
}

function ColumnsDropdown(props: ColumnsDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const identityCols: Array<{ id: string; label: string; locked?: boolean }> = [
    { id: 'asset_name', label: 'Asset name', locked: true },
    { id: 'asset_type_name', label: 'Type' },
    { id: 'system_name', label: 'System' },
    { id: 'business_unit_name', label: 'BU' },
    { id: 'owner_role_text', label: 'Owner' },
  ];
  const propertyCols: Array<{ id: string; label: string }> = [
    { id: 'external_exposure', label: 'Exposure' },
    { id: 'business_criticality', label: 'Criticality' },
    { id: 'vendor_supplied_asset', label: 'Vendor' },
  ];

  const moduleCols = (modules: ModuleCode[]): Array<{ id: string; label: string; module: ModuleCode }> =>
    modules
      .filter((m) => props.moduleScope.includes(m))
      .map((m) => ({ id: `risk_${m}`, label: moduleLabel(m), module: m }));

  const foundationCols = moduleCols([
    'timeline', 'data_risk', 'supply_chain', 'migration_feasibility', 'integrated_foundation',
  ]);
  const advancedCols = moduleCols([
    'threat', 'cryptography', 'compliance', 'integrated_advanced',
  ]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs h-7 px-2 border border-ink-200 rounded-sm bg-white hover:bg-ink-50 flex items-center gap-1"
        aria-expanded={open}
        aria-haspopup="true"
      >
        Columns
        <span className="text-ink-400">▾</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-30 bg-white border border-ink-200 rounded-sm shadow-lg p-3 w-72 max-h-[28rem] overflow-y-auto text-sm"
          role="menu"
        >
          <ColumnGroupBlock
            label="Identity"
            cols={identityCols}
            hiddenColumns={props.hiddenColumns}
            setColumnVisibility={props.setColumnVisibility}
          />
          <ColumnGroupBlock
            label="Properties"
            cols={propertyCols}
            hiddenColumns={props.hiddenColumns}
            setColumnVisibility={props.setColumnVisibility}
            collapsible={{
              groupId: 'group_properties',
              collapsed: !!props.collapsedGroups.group_properties,
              onToggle: () => props.toggleGroup('group_properties'),
            }}
          />
          {foundationCols.length > 0 && (
            <ColumnGroupBlock
              label={GROUP_LABEL.group_foundation}
              cols={foundationCols}
              hiddenColumns={props.hiddenColumns}
              setColumnVisibility={props.setColumnVisibility}
              collapsible={{
                groupId: 'group_foundation',
                collapsed: !!props.collapsedGroups.group_foundation,
                onToggle: () => props.toggleGroup('group_foundation'),
              }}
            />
          )}
          {advancedCols.length > 0 && (
            <ColumnGroupBlock
              label={GROUP_LABEL.group_advanced}
              cols={advancedCols}
              hiddenColumns={props.hiddenColumns}
              setColumnVisibility={props.setColumnVisibility}
              collapsible={{
                groupId: 'group_advanced',
                collapsed: !!props.collapsedGroups.group_advanced,
                onToggle: () => props.toggleGroup('group_advanced'),
              }}
            />
          )}
          <div className="border-t border-ink-100 mt-2 pt-2">
            <button
              type="button"
              onClick={() => {
                props.resetToDefaults();
                setOpen(false);
              }}
              className="text-xs text-accent hover:text-accent/80 hover:underline"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ColumnGroupBlockProps {
  label: string;
  cols: Array<{ id: string; label: string; locked?: boolean }>;
  hiddenColumns: Record<string, boolean>;
  setColumnVisibility: (columnId: string, visible: boolean) => void;
  collapsible?: {
    groupId: string;
    collapsed: boolean;
    onToggle: () => void;
  };
}

function ColumnGroupBlock(props: ColumnGroupBlockProps) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">
          {props.label}
        </span>
        {props.collapsible && (
          <button
            type="button"
            onClick={props.collapsible.onToggle}
            className="text-[10px] text-accent hover:underline"
          >
            {props.collapsible.collapsed ? 'Expand' : 'Collapse'}
          </button>
        )}
      </div>
      <div className="space-y-1">
        {props.cols.map((c) => {
          const visible = !props.hiddenColumns[c.id];
          return (
            <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-ink-50 px-1.5 py-0.5 rounded-sm">
              <input
                type="checkbox"
                checked={visible}
                disabled={c.locked}
                onChange={(e) => props.setColumnVisibility(c.id, e.target.checked)}
                className="h-3.5 w-3.5"
              />
              <span className={`text-xs ${c.locked ? 'text-ink-400' : 'text-ink-700'}`}>
                {c.label}
                {c.locked && <span className="ml-1 text-[10px] text-ink-300">(always)</span>}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Slice 7.9: ScrollShellWithHandles
//
// Wraps the table in a scroll container and overlays clickable left/right
// chevron buttons when the content is wider than the viewport. The chevrons
// are positioned at the vertical center of the visible scroll area so the
// user can always reach them without scrolling vertically first.
//
// We use a ResizeObserver + scroll event to recompute the "is overflowing"
// state efficiently. The handles fade in/out so they don't visually compete
// when not needed.
// =============================================================================

interface ScrollShellWithHandlesProps {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  className?: string;
  children: React.ReactNode;
}

function ScrollShellWithHandles({ scrollRef, className, children }: ScrollShellWithHandlesProps) {
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const recompute = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Slice 7.9-α2: lowered threshold from 8px to 2px so even small
    // overflows surface chevron handles. Larger thresholds were hiding
    // handles when the table overflowed by just the cut edge of a
    // partial column.
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Slice 7.9-α2: kick recompute on mount, after a tick (to let React
    // finish layout), AND on every scroll/resize. The double-recompute
    // catches the common case where the initial render's clientWidth/
    // scrollWidth haven't settled yet.
    recompute();
    const t = window.setTimeout(recompute, 50);
    el.addEventListener('scroll', recompute);
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    // Also observe direct children (the table) — when the table changes
    // width due to column visibility toggles, the scroll container's
    // size doesn't change but its scrollWidth does.
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      window.clearTimeout(t);
      el.removeEventListener('scroll', recompute);
      ro.disconnect();
    };
  }, [scrollRef, recompute]);

  const scrollBy = (delta: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    // Slice 7.9-α7: layout fix for vertical scroll regression in α5/α6.
    //
    // Pattern requirements:
    //   - Outer must be `relative` (positioning context for chevron buttons)
    //   - Outer must have `overflow-hidden` (clips chevron buttons + the
    //     absolute-inset inner so neither escapes)
    //   - Outer needs `min-h-0` because in a flex-column parent, flex
    //     children default to min-height:auto, which prevents the
    //     inner overflow-auto from ever engaging (the parent grows to
    //     accommodate content instead of constraining it)
    //   - Inner uses `absolute inset-0 overflow-auto` so it gets exactly
    //     the outer's bounds regardless of how the outer's height was
    //     computed (flex-grow, h-full, explicit, etc.)
    //   - Chevron buttons stay outside the scrolling inner so they
    //     don't scroll with content; positioned absolute relative to
    //     the outer at top-1/2
    //
    // The α5 layout was missing `overflow-hidden` and `min-h-0` on the
    // outer, which broke vertical scroll: the flex child grew to fit
    // its content (no min-h-0) and the inner overflow-auto never
    // engaged because the outer was already tall enough.
    <div className={`relative overflow-hidden min-h-0 ${className ?? ''}`}>
      <div
        ref={scrollRef}
        className="absolute inset-0 overflow-auto"
      >
        {children}
      </div>
      {canLeft && (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy(-300)}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-7 h-12 bg-white/95 hover:bg-white border border-ink-300 rounded-sm shadow-md text-ink-700 hover:text-ink-900 flex items-center justify-center"
        >
          ◀
        </button>
      )}
      {canRight && (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy(300)}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-7 h-12 bg-white/95 hover:bg-white border border-ink-300 rounded-sm shadow-md text-ink-700 hover:text-ink-900 flex items-center justify-center"
        >
          ▶
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Bulk edit panel — appears below the bulk action bar when "Bulk edit" clicked
// =============================================================================

interface BulkEditPanelProps {
  selectedIds: string[];
  onApply: (patch: Record<string, unknown>) => void;
  onCancel: () => void;
}

function BulkEditPanel({ selectedIds, onApply, onCancel }: BulkEditPanelProps) {
  const [criticality, setCriticality] = useState<string>('');
  const [exposure, setExposure] = useState<string>('');

  const apply = () => {
    const patch: Record<string, unknown> = {};
    if (criticality) patch['business_criticality'] = criticality;
    if (exposure) patch['external_exposure'] = exposure;
    if (Object.keys(patch).length === 0) return;
    onApply(patch);
  };

  return (
    <div className="border-b border-ink-200 bg-ink-50/50 px-4 py-3">
      <div className="text-xs text-ink-500 mb-2">
        Apply changes to <strong className="text-ink-700">{selectedIds.length}</strong> selected assets.
        Empty fields are not changed.
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs">Business criticality</label>
          <select
            value={criticality}
            onChange={(e) => setCriticality(e.target.value)}
            className="text-xs h-7 py-0 px-1.5 w-32"
          >
            <option value="">— unchanged —</option>
            {LIKERT_LEVELS.map((l) => (
              <option key={l} value={l}>{l.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs">External exposure</label>
          <select
            value={exposure}
            onChange={(e) => setExposure(e.target.value)}
            className="text-xs h-7 py-0 px-1.5 w-32"
          >
            <option value="">— unchanged —</option>
            {['None', 'Internal_only', 'Partner', 'Internet_facing', 'Air_gapped', 'Unknown'].map((l) => (
              <option key={l} value={l}>{l.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div className="flex-grow" />
        <button onClick={onCancel} className="btn-secondary text-xs py-1 px-3">Cancel</button>
        <button onClick={apply} className="btn-primary text-xs py-1 px-3">Apply</button>
      </div>
    </div>
  );
}

// =============================================================================
// ModuleInputsPopover — shows the module's hidden input parameters when the
// user hovers over the module score column header.
//
// "Hidden" here means "feeds the module score but isn't shown in the
// AssetGrid as a column" — the user has to open the asset deep-edit form
// to see/edit those values. The popover lets them know what's there
// without forcing the click.
// =============================================================================

interface ModuleInputsPopoverProps {
  module: ModuleCode;
}

function ModuleInputsPopover({ module }: ModuleInputsPopoverProps) {
  const inputs = MODULE_INPUTS[module];
  if (!inputs || inputs.hiddenInputs.length === 0) return null;
  return (
    <div
      className="absolute z-30 mt-1 bg-white border border-ink-200 shadow-md rounded-sm
                 p-3 text-[10px] normal-case tracking-normal w-[260px] left-0 top-full
                 pointer-events-none"
      role="tooltip"
    >
      <div className="text-ink-500 mb-1.5 font-semibold uppercase tracking-wider">
        Also feeds from
      </div>
      <ul className="space-y-1 text-ink-700">
        {inputs.hiddenInputs.map((p) => (
          <li key={p} className="flex items-baseline gap-1.5">
            <span className="text-ink-300 text-[8px]">▸</span>
            <span>{parameterLabel(p)}</span>
          </li>
        ))}
      </ul>
      <div className="text-ink-400 mt-2 pt-2 border-t border-ink-100 text-[9px] italic">
        Edit these fields in the asset detail form.
      </div>
    </div>
  );
}